<template lang="pug">

    .card

        .content(v-if="specimenCopyModel.id")
            i.right.floated.link.pencil.icon(v-if='isEditable' v-tooltip="'Belegkopie ändern'" v-on:click="showModal()")
            .header {{ headline }}
            .meta(v-if='showAddressName')
                router-link.ui.link(target='_blank' v-bind:to='routerLink') {{ addressModel.name }}
            .description
                .ui.list
                    .item(v-if="!isPrivate") {{ addressModel.firm }}
                    .item(v-if="!isPrivate") {{ addressModel.street }}
                    .item {{ addressModel.zip }} {{ addressModel.city }}

        .content(v-else)
            i.right.floated.link.pencil.icon(v-if='isEditable' v-tooltip="'Adresse ändern'" v-on:click="showModal()")
            .header {{ headline }}
            .description
                i Keine Adresse angegeben

        .extra.content(v-if="specimenCopyModel.id")
            .ui.list
                .item
                    | #[strong Anzahl: ] {{ amount }}
                .item(v-if="recipientModel")
                    | #[strong Empfänger: ] {{ recipientName }}

        modal(ref="modal" v-if='isEditable')
            span(slot="header") Belegexemplare bearbeiten

            div(slot="content").ui.form
                specimen-copy-form(
                    ref="form"
                    label='Lieferadresse'
                    v-bind:address-models='addressModels'
                    v-bind:company-model='companyModel'
                    v-bind:country-models='countryModels'
                    v-bind:person-models='personModels'
                    v-bind:recipient-person-models='recipientPersonModels'
                    v-model="model"
                )

            action-row(
                slot="footer"
                save-type='button'
                :disabled-save-button='disabledSaveButton'
                v-on:cancel="cancel()"
                v-on:save="submit()"
            )
                a.ui.red.labeled.icon.button(v-on:click='remove()')
                    i.trash.icon
                    | Entfernen

</template>

<script>
    import * as R from 'ramda'
    import store from '@/store'
    import ActionRow from '@/components/form/ActionRow'
    import DropdownField from '@/components/form/DropdownField'
    import Modal from '@/components/app/Modal'
    import SpecimenCopyForm from '@/components/products/forms/SpecimenCopyForm'

    // noinspection ReservedWordAsName
    export default {

        components: { ActionRow, DropdownField, Modal, SpecimenCopyForm },

        computed: {

            addressModel() {
                return R.compose(

                    // ggf. Default setzen
                    R.defaultTo({}),

                    // Adresse finden
                    R.find(R.propEq('id', this.specimenCopyModel.address))

                )(this.addressModels)
            },

            amount() {
                return R.prop('amount', this.specimenCopyModel)
            },

            recipientModel() {
                return store.getters['persons/byId'](this.specimenCopyModel.recipient)
            },

            recipientName() {
                return this.recipientModel
                    ? `${this.recipientModel.firstName} ${this.recipientModel.lastName}`
                    : ''
            },

            disabledSaveButton() {
                return R.isNil(this.model.amount) || R.isNil(this.model.address)
            },

            headline() {
                return this.isInternal ? 'Belegexemplare (Intern)' : 'Belegexemplar'
            },

            id: {
                get() { return R.prop('id', this.model) },
                set(addressId) {
                    this.model = store.getters['addresses/byId'](addressId)
                }
            },

            routerLink() {
                return {
                    name: 'addresses/update',
                    params: { id: this.addressModel.id }
                }
            },

        },

        created() {
            this.model = { ...this.specimenCopyModel }
        },

        data() {
            return { model: {} }
        },

        methods: {
            cancel() {
                this.$refs.modal.close()
            },

            create() {
                const productId  = this.productModel.id
                const model      = this.model
                const isInternal = this.isInternal

                store.dispatch('specimenCopies/create', { productId, model, isInternal }).then(() => {
                    this.$refs.modal.close()
                    this.$growl.success('Belegexemplar erstellt')
                }).catch(() => {
                    this.$growl.error('Belegexemplar konnte nicht erstellt werden')
                })
            },

            remove() {
                store.dispatch('specimenCopies/remove', { model: this.model }).then(() => {
                    this.$refs.modal.close()
                    this.$growl.success('Belegexemplar entfernt')
                }).catch(() => {
                    this.$growl.error('Belegexemplar konnte nicht entfernt werden')
                })
            },

            submit() {
                this.validate()
                    .then(R.when(R.equals(true), R.ifElse(
                        () => R.isNil(this.model.id), this.create, this.update
                    )))
            },

            showModal() {
                this.$refs.modal.show()
            },

            update() {
                store.dispatch('specimenCopies/update', { model: this.model }).then(() => {
                    this.$refs.modal.close()
                    this.$growl.success('Belegexemplar aktualisiert')
                }).catch(() => {
                    this.$growl.error('Belegexemplar konnte nicht aktualisiert werden')
                })
            },

            validate() {
                return this.$refs.form.validate()
            }

        },

        props: {

            addressModels: {
                type: Array,
                required: true
            },

            companyModel: {
                type: Object,
                required: true
            },

            countryModels: {
                type: Array,
                required: false
            },

            isInternal: {
                type: Boolean,
                required: true
            },

            isEditable: {
                type: Boolean,
                default: true
            },

            isPrivate: {
                type: Boolean,
                default: false
            },

            personModels: {
                type: Array,
                required: false
            },

            recipientPersonModels: {
                type: Array,
                required: false
            },

            productModel: {
                type: Object,
                required: true
            },

            showAddressName: {
                type: Boolean,
                default: true
            },

            specimenCopyModel: {
                type: Object,
                required: true
            }

        },

        watch: {
            specimenCopyModel(model) {
                this.model = { ...model }
            }
        }

    }

</script>
